import { FormGroup } from "@blueprintjs/core";
import { DataManager, Predicate } from "@syncfusion/ej2-data";
import { MultiSelectComponent } from "@syncfusion/ej2-react-dropdowns";
import React from "react";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";

import {
  AsyncMultiselectFieldValue,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { useAsyncMultiselectField } from "./useAsyncMultiselectField";
import { AsyncMultiselectProps } from "../../types/fieldTypes";
import { AsyncODataFieldProps } from "../types";
import { LoadingV2 } from "@ucl/library";
import classNames from "classnames";

export interface AsyncMultiselectFieldProps
  extends AsyncMultiselectProps,
    AsyncODataFieldProps {
  dataSource?: DataManager;
  value: AsyncMultiselectFieldValue;
  allowableSelectionCount?: number;
  conditions?: Predicate[];
  optionFields?: string[];
  noRecordsTemplate?: (ref: any) => React.ReactNode;
  fieldNames?: string | string[];
  groupByActiveStatus?: boolean;
  labelProps?: FieldLabeledProps;
  transformData?: (data: any[]) => any[];
  closePopupOnSelect?: boolean;
}

const AsyncMultiselectField: React.FC<AsyncMultiselectFieldProps> = (props) => {
  const {
    dataSource,
    multiSelectRef,
    change,
    focus,
    fields,
    created,
    open,
    close,
    query,
  } = useAsyncMultiselectField(props);

  if (!dataSource) {
    return <LoadingV2 />;
  }

  return (
    <FormGroup
      className={classNames({
        "has-required-background":
          (props.isRequired && !props.value) ||
          (props.isRequired && !!props.value && props.value.length === 0) ||
          (props.isRequired &&
            !!props.value &&
            props.value.filter((x) => x !== null && x !== undefined).length ===
              0),
        "ucl-async-multiselect-field": true,
      })}
    >
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: props.value }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <div className="ucl-async-multiselect-toolbar">
        <div className="ucl-async-multiselect-input-container">
          <MultiSelectComponent
            className="ucl-async-multiselect-input"
            ref={multiSelectRef}
            dataSource={dataSource}
            value={props.value} // this is a controlled component.
            created={created}
            change={change}
            open={open}
            close={close}
            focus={focus}
            openOnClick={true}
            closePopupOnSelect={props.closePopupOnSelect ?? false}
            placeholder={props.placeholder ?? "Type to search..."}
            enabled={!props.disabled}
            allowFiltering={true}
            mode="Box"
            changeOnBlur={true}
            ignoreCase={true}
            sortOrder="Ascending"
            maximumSelectionLength={props.allowableSelectionCount}
            showClearButton={true}
            fields={fields}
            itemTemplate="<span class='ucl-async-autocomplete-item ucl-async-autocomplete-item-disabled-${isDeleted}'><table><tr><td>${label}</td></tr><table></span>"
            query={query}
          />
        </div>
      </div>
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </FormGroup>
  );
};

export default AsyncMultiselectField;
